import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  HandHeart, CalendarClock, FileText, DollarSign, AlertTriangle,
  Volume2, VolumeX, CheckCircle2, ListChecks, ShieldCheck, Info,
  Send, MessageCircle, Loader2, HelpCircle,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getSharedView, supporterConfirm, sharedImageUrl, sendThankYou, updateSupporterStatus } from "@/lib/api";
import { speak, stop, ttsSupported } from "@/lib/tts";
import { Textarea } from "@/components/ui/textarea";

const URGENCY = {
  high: "This letter asks for a fairly quick response.",
  medium: "There's some urgency, but time to think.",
  low: "Not urgent — take your time.",
  unclear: "Urgency isn't clear from this letter.",
};

function formatDeadline(a) {
  if (a?.deadline_text) return a.deadline_text;
  if (a?.deadline) {
    try { return new Date(a.deadline + "T00:00:00").toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); } catch { return a.deadline; }
  }
  return "No deadline set";
}

export default function Shared() {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [reading, setReading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [thankYouMessage, setThankYouMessage] = useState("");
  const [sendingThank, setSendingThank] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    let alive = true;
    getSharedView(shareToken)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => setErr(e?.response?.data?.detail || "This support link is not valid."));
    return () => { alive = false; stop(); };
  }, [shareToken]);

  const analysis = data?.analysis || {};
  const notif = data?.notifications || {};
  const seniorName = notif.senior_first_name || "Your loved one";
  const level = data?.share_level || "summary";

  const readText = level === "action_only"
    ? `${seniorName} used LetterLifts and asked for your help. Action needed: ${analysis.required_action}. Date: ${formatDeadline(analysis)}.`
    : `${seniorName} used LetterLifts and asked for your help. ${analysis.plain_language_summary || ""}. Action: ${analysis.required_action}. Date: ${formatDeadline(analysis)}.`;

  const doRead = () => {
    if (reading) { stop(); setReading(false); return; }
    if (!ttsSupported()) { toast.error("Read Aloud isn't available in this browser."); return; }
    speak(readText, { onStart: () => setReading(true), onEnd: () => setReading(false), onError: () => setReading(false) });
  };

  const iWillHelp = async () => {
    setConfirming(true);
    try {
      const r = await supporterConfirm(shareToken);
      // Also refresh the full view so can_send_thank_you flips true
      const refreshed = await getSharedView(shareToken).catch(() => null);
      setData((d) => ({
        ...(d || {}),
        ...(refreshed || {}),
        supporter_confirmed: true,
        notifications: {
          ...(d?.notifications || {}),
          ...(refreshed?.notifications || {}),
          supporter_confirmed_at: r.supporter_confirmed_at,
        },
      }));
      toast.success("Thank you — the senior will see that you'll help.");
      // Prefill a warm default so the button is a one-tap send
      if (!thankYouMessage) {
        const defaultMsg = `Got your LetterLifts note — I've got this handled. I'll follow up with you soon. Love you.`;
        setThankYouMessage(defaultMsg);
      }
    } catch { toast.error("Couldn't confirm right now."); }
    finally { setConfirming(false); }
  };

  const doSendThankYou = async () => {
    const msg = thankYouMessage.trim();
    if (!msg) { toast.error("Please write a short note first."); return; }
    setSendingThank(true);
    try {
      const r = await sendThankYou(shareToken, msg);
      if (r.thank_you_status === "sent") {
        toast.success("Note sent — they'll know you've got this.");
        setData((d) => ({
          ...(d || {}),
          notifications: {
            ...(d?.notifications || {}),
            thank_you_status: "sent",
            thank_you_sent_at: r.thank_you_sent_at,
            can_send_thank_you: false,
          },
        }));
      } else {
        toast.error(r.thank_you_error || "We couldn't send this note — please try again.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "We couldn't send this note — please try again.");
    } finally { setSendingThank(false); }
  };

  const doUpdateStatus = async (nextStatus) => {
    setUpdatingStatus(true);
    try {
      const r = await updateSupporterStatus(shareToken, nextStatus);
      setData((d) => ({
        ...(d || {}),
        notifications: {
          ...(d?.notifications || {}),
          status: r.status,
          status_updated_at: r.status_updated_at,
        },
      }));
      const label = nextStatus === "in_progress" ? "In progress" : nextStatus === "completed" ? "Completed" : "Needs help";
      toast.success(`Status updated: ${label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update the status right now.");
    } finally { setUpdatingStatus(false); }
  };

  if (err) return (
    <>
      <Header />
      <main className="mx-auto max-w-2xl px-6 py-16 text-center" data-testid="shared-error">
        <h1 className="font-display text-3xl font-bold">Link not available</h1>
        <p className="mt-3 text-lg text-slate-700">{err}</p>
      </main>
      <Footer />
    </>
  );
  if (!data) return (<><Header /><main className="mx-auto max-w-2xl px-6 py-24 text-center"><p className="text-xl text-slate-600">Loading…</p></main></>);

  const familyStatusLabel = notif.family_email_status === "sent" ? "Family notified" : "Family email pending";
  const seniorReminderLabel = notif.senior_email_status === "sent" ? "Reminder sent" : "Reminder not sent";

  return (
    <>
      <Header />
      <main data-testid="shared-page" className="mx-auto max-w-4xl px-6 py-10">
        <div className="rounded-3xl border-2 border-moss-700 bg-moss-50/60 p-5 flex items-start gap-3" data-testid="shared-privacy-banner">
          <ShieldCheck strokeWidth={2.5} className="mt-0.5 h-5 w-5 text-moss-700 flex-shrink-0" />
          <p className="text-base text-moss-900">
            You're seeing this because <b>{seniorName}</b> chose to share it with you. This page is read-only,
            and it will expire after the deadline. It's not a login — you don't have an account.
          </p>
        </div>

        <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-moss-700">Someone you know needs a hand</p>
            <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold text-slate-900" data-testid="shared-title">
              {seniorName} asked for your support
            </h1>
            {analysis.document_type && (
              <p className="mt-2 text-lg text-slate-600" data-testid="shared-doc-type">About: {analysis.document_type}</p>
            )}
          </div>
          <button onClick={doRead} className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-lg font-bold min-h-[56px] ${reading ? "bg-red-700 text-bone cn-pulse" : "bg-moss-700 text-bone hover:bg-moss-800"}`} data-testid="shared-read-aloud">
            {reading ? <><VolumeX strokeWidth={2.5} className="h-5 w-5" /> Stop</> : <><Volume2 strokeWidth={2.5} className="h-5 w-5" /> Read Aloud</>}
          </button>
        </div>

        {/* Cards */}
        <section className="mt-8 grid gap-5" data-testid="shared-cards">
          {level !== "action_only" && analysis.plain_language_summary && (
            <ShareCard icon={FileText} title="What this is about" body={analysis.plain_language_summary} testId="shared-card-summary" />
          )}
          <ShareCard icon={HandHeart} title="What needs to happen" body={analysis.required_action || "No action found."} testId="shared-card-action" />
          <ShareCard icon={CalendarClock} title="Important date" body={formatDeadline(analysis)} testId="shared-card-date" />
          {level !== "action_only" && analysis.amount && (
            <ShareCard icon={DollarSign} title="Amount" body={analysis.amount} testId="shared-card-amount" />
          )}
          <ShareCard icon={AlertTriangle} title="How urgent" body={URGENCY[analysis.urgency] || URGENCY.unclear} testId="shared-card-urgency" />

          {level === "summary" || level === "full_support" ? (
            analysis.important_points?.length > 0 && (
              <div className="rounded-3xl border-2 border-slate-200 bg-white p-6 sm:p-8" data-testid="shared-card-important-points">
                <div className="flex items-center gap-3">
                  <span className="grid h-11 w-11 place-items-center rounded-2xl bg-moss-700 text-bone"><ListChecks strokeWidth={2.5} className="h-5 w-5" /></span>
                  <h3 className="font-display text-2xl font-bold">Key points</h3>
                </div>
                <ul className="mt-4 space-y-2 text-lg leading-relaxed text-slate-800">
                  {analysis.important_points.map((p, i) => (<li key={i} className="flex gap-3"><span className="text-moss-700">•</span><span>{p}</span></li>))}
                </ul>
              </div>
            )
          ) : null}

          {level === "full_support" && data.include_image && (
            <div className="rounded-3xl border-2 border-slate-200 bg-white p-4" data-testid="shared-letter-image">
              <p className="px-2 pt-2 text-sm font-semibold uppercase tracking-wider text-moss-700">The original letter</p>
              <img src={sharedImageUrl(shareToken)} alt="Original letter" className="mt-3 max-h-[560px] w-full rounded-2xl border border-slate-200 object-contain" />
            </div>
          )}
        </section>

        {/* Status + supporter action */}
        <section className="mt-10 rounded-3xl border-2 border-slate-200 bg-white p-6 sm:p-8" data-testid="shared-status-card">
          <div className="flex flex-wrap items-center gap-4 text-lg">
            <span className="inline-flex items-center gap-2 rounded-full bg-moss-50 px-4 py-2 text-moss-900 border-2 border-moss-700" data-testid="shared-family-status">
              {notif.family_email_status === "sent" ? <CheckCircle2 className="h-5 w-5" /> : <Info className="h-5 w-5" />}
              {familyStatusLabel}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 border-2 border-slate-300 text-slate-700" data-testid="shared-senior-status">
              {notif.senior_email_status === "sent" ? <CheckCircle2 className="h-5 w-5 text-moss-700" /> : <Info className="h-5 w-5" />}
              {seniorReminderLabel}
            </span>
            {data.supporter_confirmed && (
              <span className="inline-flex items-center gap-2 rounded-full bg-moss-700 px-4 py-2 text-bone" data-testid="shared-supporter-confirmed">
                <CheckCircle2 className="h-5 w-5" /> Trusted supporter confirmed
              </span>
            )}
          </div>

          {!data.supporter_confirmed ? (
            <button onClick={iWillHelp} disabled={confirming} className="mt-6 inline-flex items-center gap-3 rounded-full bg-moss-700 px-8 py-4 text-lg font-bold text-bone hover:bg-moss-800 disabled:opacity-60 min-h-[56px]" data-testid="ill-help-btn">
              <HandHeart strokeWidth={2.5} className="h-6 w-6" />
              {confirming ? "Confirming…" : "I'll Help"}
            </button>
          ) : (
            <div className="mt-6" data-testid="status-picker">
              <p className="text-lg text-moss-800">
                Thank you. {seniorName} will see where things stand.
              </p>
              <p className="mt-5 text-sm font-semibold uppercase tracking-[0.14em] text-moss-700">
                Update {seniorName} on where you're at
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-3" role="group" aria-label="Update status">
                {[
                  { key: "in_progress", label: "In progress", Icon: Loader2,      className: "bg-amber-50 text-amber-900 border-amber-400 hover:bg-amber-100" },
                  { key: "completed",   label: "Completed",   Icon: CheckCircle2, className: "bg-emerald-50 text-emerald-900 border-emerald-400 hover:bg-emerald-100" },
                  { key: "needs_help",  label: "I need help", Icon: HelpCircle,   className: "bg-rose-50 text-rose-900 border-rose-400 hover:bg-rose-100" },
                ].map((opt) => {
                  const active = (notif.status || "accepted") === opt.key;
                  const Icon = opt.Icon;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => doUpdateStatus(opt.key)}
                      disabled={updatingStatus || active}
                      data-testid={`status-btn-${opt.key}`}
                      className={`inline-flex items-center justify-center gap-2 rounded-2xl border-2 px-4 py-4 text-lg font-bold transition-colors disabled:cursor-not-allowed ${
                        active
                          ? "border-slate-900 bg-slate-900 text-bone"
                          : opt.className
                      } ${updatingStatus && !active ? "opacity-60" : ""}`}
                    >
                      <Icon strokeWidth={2.5} className={`h-5 w-5 ${active && opt.key === "in_progress" ? "animate-spin" : ""}`} />
                      {active ? `✓ ${opt.label}` : opt.label}
                    </button>
                  );
                })}
              </div>
              {notif.status && notif.status !== "accepted" && (
                <p className="mt-3 text-sm text-slate-600" data-testid="status-current-label">
                  Current status: <b>{notif.status.replace("_", " ")}</b>
                  {notif.status_updated_at ? ` · updated ${new Date(notif.status_updated_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : ""}
                </p>
              )}
            </div>
          )}
        </section>

        {/* Thank-you note card — only shows after "I'll Help" and if senior gave an email */}
        {data.supporter_confirmed && (notif.can_send_thank_you || notif.thank_you_status === "sent") && (
          <section
            className="mt-8 rounded-3xl border-2 border-ember-600/40 bg-gradient-to-br from-ember-50 via-white to-white p-6 sm:p-8"
            data-testid="thank-you-card"
          >
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-2xl bg-ember-700 text-bone">
                <MessageCircle strokeWidth={2.5} className="h-5 w-5" />
              </span>
              <h2 className="font-display text-2xl font-bold text-slate-900">Send {seniorName} a note back</h2>
            </div>

            {notif.thank_you_status === "sent" ? (
              <div className="mt-5" data-testid="thank-you-sent">
                <p className="text-lg text-slate-800">
                  <span className="inline-flex items-center gap-2 rounded-full bg-moss-700 px-3 py-1 text-sm font-bold text-bone">
                    <CheckCircle2 className="h-4 w-4" /> Note sent
                  </span>
                </p>
                <p className="mt-3 text-lg text-slate-700">
                  {seniorName} will see your message on their end. Nice touch.
                </p>
              </div>
            ) : (
              <div className="mt-5 space-y-4">
                <p className="text-lg leading-relaxed text-slate-700">
                  A short, warm note lets {seniorName} know their message arrived — and that they're
                  not doing this alone. It'll be emailed to them right now.
                </p>
                <Textarea
                  value={thankYouMessage}
                  onChange={(e) => setThankYouMessage(e.target.value)}
                  rows={5}
                  maxLength={1200}
                  placeholder={`Hi ${seniorName}, got your note — I've got this handled.`}
                  className="w-full text-lg border-2 border-slate-300 rounded-2xl p-4 bg-white"
                  data-testid="thank-you-textarea"
                />
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={doSendThankYou}
                    disabled={sendingThank || !thankYouMessage.trim()}
                    className="inline-flex items-center gap-3 rounded-full bg-ember-700 px-7 py-4 text-lg font-bold text-bone hover:bg-ember-600 disabled:opacity-60 min-h-[56px]"
                    data-testid="send-thank-you-btn"
                  >
                    <Send strokeWidth={2.5} className="h-5 w-5" />
                    {sendingThank ? "Sending…" : `Send this to ${seniorName}`}
                  </button>
                  <span className="text-sm text-slate-500">{thankYouMessage.length}/1200</span>
                </div>
                {notif.thank_you_status === "failed" && notif.thank_you_error && (
                  <p className="text-sm text-red-700" data-testid="thank-you-error">
                    {notif.thank_you_error}
                  </p>
                )}
              </div>
            )}
          </section>
        )}

        {data.supporter_confirmed && !notif.can_send_thank_you && notif.thank_you_status !== "sent" && (
          <p className="mt-6 text-center text-sm text-slate-500" data-testid="thank-you-unavailable">
            {seniorName} didn't share an email, so we can't deliver a note back to them right now.
          </p>
        )}

        <p className="mt-10 text-center text-sm text-slate-500">
          General document assistance — not legal, medical, or financial advice.
        </p>
      </main>
      <Footer />
    </>
  );
}

function ShareCard({ icon: Icon, title, body, testId }) {
  return (
    <article className="cn-card-lift rounded-3xl border-2 border-slate-200 bg-white p-6 sm:p-8" data-testid={testId}>
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-moss-700 text-bone"><Icon strokeWidth={2.5} className="h-5 w-5" /></span>
        <h3 className="font-display text-2xl font-bold text-slate-900">{title}</h3>
      </div>
      <p className="mt-4 text-xl leading-relaxed text-slate-800">{body}</p>
    </article>
  );
}
